import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../lib/types/auth';
import { subscribeToFloater } from '../../services/holidayFloaterService';

const RISE_MS = 9000;
/** A send older than this when it arrives (reconnect, late page load) is not shown. */
const MAX_AGE_MS = 15_000;

interface Floater {
  id: string;
  character: string;
  leftPct: number;
}

const FloatingCharacter: React.FC<Floater & { onDone: (id: string) => void }> = ({ id, character, leftPct, onDone }) => {
  const riseRef = useRef<HTMLDivElement>(null);
  const swayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const rise = riseRef.current;
    const sway = swayRef.current;
    if (!rise || !sway) return;

    const height = window.innerHeight + 320;
    const drift = () => `${Math.round((Math.random() - 0.5) * window.innerWidth * 0.3)}px`;
    const riseAnim = rise.animate(
      [
        { transform: 'translate(0px, 0px)', opacity: 0 },
        { transform: `translate(${drift()}, ${-height * 0.25}px)`, opacity: 0.55, offset: 0.12 },
        { transform: `translate(${drift()}, ${-height * 0.5}px)`, opacity: 0.55, offset: 0.45 },
        { transform: `translate(${drift()}, ${-height * 0.75}px)`, opacity: 0.55, offset: 0.75 },
        { transform: `translate(${drift()}, ${-height}px)`, opacity: 0.45 },
      ],
      { duration: RISE_MS, easing: 'ease-in-out', fill: 'forwards' },
    );
    const swayAnim = sway.animate(
      [
        { transform: 'translateX(-30px) rotate(-10deg)' },
        { transform: 'translateX(30px) rotate(10deg)' },
      ],
      { duration: 1600, direction: 'alternate', iterations: Infinity, easing: 'ease-in-out' },
    );
    riseAnim.onfinish = () => onDone(id);
    return () => {
      riseAnim.cancel();
      swayAnim.cancel();
    };
  }, [id, onDone]);

  return (
    <div ref={riseRef} className="absolute -bottom-40" style={{ left: `${leftPct}%`, opacity: 0 }}>
      <div ref={swayRef} className="text-[120px] leading-none select-none drop-shadow-[0_0_24px_rgba(255,255,255,0.6)]">
        {character}
      </div>
    </div>
  );
};

/** Shows the holiday character a Master Admin sends, for users with floaterAccess and Master Admins. */
export const HolidayFloaterListener: React.FC = () => {
  const { currentUser } = useAuth();
  const canSee = !!currentUser && (currentUser.roles?.includes(UserRole.MasterAdmin) || currentUser.floaterAccess === true);
  const [floaters, setFloaters] = useState<Floater[]>([]);
  const seen = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!canSee) return;
    return subscribeToFloater((t) => {
      const sentMs = t.sentAt?.toMillis?.();
      if (!sentMs || seen.current.has(t.nonce)) return;
      seen.current.add(t.nonce);
      if (Date.now() - sentMs > MAX_AGE_MS) return;
      setFloaters((list) => [...list, { id: t.nonce, character: t.character, leftPct: 10 + Math.random() * 75 }]);
    });
  }, [canSee]);

  const remove = React.useCallback((id: string) => setFloaters((list) => list.filter((f) => f.id !== id)), []);

  if (!canSee || floaters.length === 0) return null;
  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none overflow-hidden" aria-hidden="true">
      {floaters.map((f) => (
        <FloatingCharacter key={f.id} {...f} onDone={remove} />
      ))}
    </div>
  );
};

export default HolidayFloaterListener;
